/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {batch, useDispatch, useSelector} from 'react-redux';
import {ColDef, ColumnState} from 'ag-grid-community';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {useSystemOfUnits} from '@ion/applications/src/hooks/useSystemOfUnits';
import {
  getAssetState,
  getAssetStateLabel,
  getBattery,
  getBatteryVoltageFixed
} from '@ion/core/src/entities/Asset';
import {
  normalizeDriverName
} from '@ion/core/src/entities/Drivers';
import {translate} from '@ion/core/src/locale/locale';
import Device from '@ion/core/src/services/Device';
import {
  EventCode,
  EventCodeValueType,
  getEventList,
  getEventName,
  getEventType
} from '@ion/core/src/services/EventCodes';
import {
  AssetType,
  CVSAssetType,
  GenericObject,
  SCDeviceType
} from '@ion/core/src/types/entities';
import {convertAVLEvent, getEventSpeed, isMetric} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {
  booleanToSwitchString
} from '@ion/core/src/utils/BooleanUtils';
import {
  dateFormatter,
  secondsToHours
} from '@ion/core/src/utils/DateUtil';
import {
  toFixed
} from '@ion/core/src/utils/NumberUtil';
import {
  titleCase
} from '@ion/core/src/utils/StringUtil';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import {
  caseInSensitiveEqualsFilterParams,
  ClientISODateFilterParams,
  defaultCVSFilterParams,
  defaultTextContainsFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {
  definitionsToState, resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {
  AgGridType, IonGrid, IonGridAPI, RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {
  filterData,
  getDisplayedRows,
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  useColumnChooser
} from '@ion/dls/src/grid/useColumnChooser';
import {
  useColumnState
} from '@ion/dls/src/grid/useColumnState';
import useMedia from '@ion/dls/src/hooks/useMedia';
import {getSpeedByStatus} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {
  clientGridDateTimeComparator,
  dateTimeFormatter,
  formatAddress
} from '@ion/dls/src/utils/ComponentUtils';
import {
  onAssetRowClicked
} from '@ion/dls/src/utils/GridUtils';
import Features from '@/security/Features';
import {exportTrackingAssets} from '@/store/actions/exporter';
import {dispatchGridColumnsChange} from '@/store/actions/helpers';
import {
  NAME,
  onAssetSelected,
  SET_GRID_COLUMNS,
  setFilterModel,
  setGridChange,
  setGridFilteredAssetIds,
  setIsAssetGridUpdate,
  setSelectedAsset
} from '@/store/actions/tracking';
import {ascendingOrderValidator, mergeLatestUpdates} from '@/store/helpers';
import {
  desktopTopSheetOpenSelector
} from '@/store/selectors/search-selectors';
import {
  assetsUpdatesSelector,
  findNearbyIsActiveSelector,
  getListAssetsSelector,
  gridColumnStateSelector,
  gridFilterModelSelector,
  scDevicesLoadedSelector,
  scDevicesUpdatesSelector
} from '@/store/selectors/tracking-selectors';
import {useAssetListState} from '../../hooks/useAssetListState';
import AssetTypeCell from '../admin/assets/AssetTypeCell';
import {SpeedCell} from '../admin/assets/SpeedCell';
import {useScrollToItem} from './hooks/grid/useScrollToItem';
import useTrackingAssetCount from './hooks/sidesheet/useTrackingAssetCount';
import {useTrackingGridStyles} from './TrackingAssetServerGrid';
import {
  columnNormalizer,
  columnResolver,
  distanceColumnName,
  filterModelConverterV2,
  getAssociations,
  getEngineType,
  getInputChangedEvents,
  getInputsOnOffLabel,
  getItemValue,
  scrollToInterval,
  sortModelConverter,
  verifyDistanceColumn
} from './trackingUtils';

export enum HiddenColumns {
  distance = distanceColumnName
};

// prop definitions for asset history grid component.
export interface TrackingAssetGridProps {
  assets?: Device[];
  assetList?: Device[];
  isLargeFleetMode?: boolean;
  height?: number;
  includesAssets?: boolean;
  includesScDevices?: boolean;
  loading?: boolean;
  isFilterSelected?: boolean;
  isDesktopTopSheetOpen?: boolean;
}

const attributeMappings = {
  operatorNamesUI         : 'operatorNames',
  assetType               : 'assetType',
  eventTimeUI             : 'eventTime',
  latestEventUI           : 'eventType',
  eventTypeUI             : 'eventType',
  odometerUI              : 'odometer',
  gpsOdometerUI           : 'gpsOdometer',
  vbOdometerUI            : 'vbOdometer',
  engineHourUI            : 'engineHour',
  engineHoursGPSUI        : 'engineHours',
  fuelTypeUI              : 'fuelType',
  idleHoursUI             : 'idleHours',
  engineLightUI           : 'vbusMilStatus',
  vbatIntUI               : 'adcDeviceBatteryVoltage',
  vbatExtUI               : 'adcExternalPowerVoltage',
  ignitionUI              : 'ignition',
  input1UI                : 'input1',
  input2UI                : 'input2',
  input3UI                : 'input3',
  input4UI                : 'input4',
  input5UI                : 'input5',
  input6UI                : 'input6',
  input7UI                : 'input7',
  gpsValidityUI           : 'gpsValidity',
  commStateAvailableUI    : 'commStateAvailable',
  gpsSpeedUI              : 'gpsSpeed',
  vbSpeedUI               : 'vbSpeed',
  latitudeUI              : 'latitude',
  longitudeUI             : 'longitude',
  speedUI                 : 'speed',
  addressUI               : 'street',
  altitudeUI              : 'altitude',
  routeNameUI             : 'routeName',
  assetNameUI             : 'assetName',
  deviceEsnUI             : 'deviceIdValue',
  location                : 'location',
  deviceType              : 'deviceType',
  batteryRemainingPercent : 'batteryRemainingPercent'
};

// would like to sort by assignment, unchecked at top
const defaultSort: SortModelType = {
  colId : 'assetNameUI',
  sort  : 'asc'
};

// Sort comparator
const sortComparator = (valueA: string, valueB: string) => {
  const vA = valueA?.trim()?.toLowerCase();
  const vB = valueB?.trim()?.toLowerCase();
  return ascendingOrderValidator(vA, vB);
};

/**
   * @TrackingAssetGrid
   *
   * Grid to be rendered on the Admin Roles page.
   */
export const TrackingAssetGrid = ({
  assetList,
  height,
  isLargeFleetMode = false,
  isFilterSelected = false
}: TrackingAssetGridProps, ref) => {
  const dispatch                              = useDispatch();
  const {isDesktop, isPhone}                  = useMedia();
  const assetsRef                             = useRef(null);
  const mediaRef                              = useRef({isDesktop, isPhone});
  const eventListRef                          = useRef(getEventList());
  const ionGridRef                            = useRef<IonGridAPI>();
  const assetsListRef                         = useRef(null);
  const [gridApi, setGridApi]                 = useState<AgGridType>(null);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const showAssetsListRef                     = useAssetListState();
  const columnState: ColumnState[]            = useSelector(gridColumnStateSelector);
  const filterModel                           = useSelector(gridFilterModelSelector);
  const assetsUpdates                         = useSelector(assetsUpdatesSelector);
  const scDevicesUpdates                      = useSelector(scDevicesUpdatesSelector);
  const isDesktopTopSheetOpen                 = useSelector(desktopTopSheetOpenSelector);
  const findNearbyIsActive                    = useSelector(findNearbyIsActiveSelector);
  const hasDevicesLoaded                      = useSelector(scDevicesLoadedSelector);
  const unitsInHeader                         = useSystemOfUnits();
  const hasDeviceLoadedRef                    = useRef(hasDevicesLoaded);
  const {
    data: assets,
    loading,
    includesAssets,
    includesScDevices
  } = useSelector(getListAssetsSelector);
  const [assetsList, setAssetsList] = useState<Device[]>([]);
  const findNearbyIsActiveRef       = useRef(findNearbyIsActive);
  const [isLoading, setIsLoading]   = useState(false);
  const onLoading                   = useTrackingAssetCount();
  const {
    selectedAsset,
    rowClicked,
    scrollToItem
  } = useScrollToItem(gridApi, scrollToInterval.clientMode);
  const selectedAssetRef = useRef(selectedAsset);

  const hasDevicesAccess    = useMemo(() => Features.hasSCIAccess(), [Features.hasSCIAccess()]);
  const hasDevicesAccessRef = useRef(hasDevicesAccess);

  useEffect(() => {
    selectedAssetRef.current = selectedAsset;
  }, [selectedAsset]);

  useEffect(() => {
    hasDeviceLoadedRef.current = hasDevicesLoaded;
  }, [hasDevicesLoaded]);

  useEffect(() => {
    hasDevicesAccessRef.current = hasDevicesAccess;
  }, [hasDevicesAccess]);

  useEffect(() => {
    if (loading) {
      setIsLoading(true);
    } else {
      const timeout = setTimeout(() => {
        setIsLoading(false);
        clearTimeout(timeout);
      }, 1000);
    }
  }, [loading]);

  useEffect(() => {
    findNearbyIsActiveRef.current = findNearbyIsActive;
  }, [findNearbyIsActive]);

  useEffect(() => {
    if (!findNearbyIsActive) {
      const columns = resolveDefinitions(getColumnDef({
        includesAssets,
        includesScDevices,
        findNearbyIsActive,
        unitsInHeader,
        sortComparator
      }));
      gridApi?.setGridOption('columnDefs', columns);
    }
  }, [findNearbyIsActive]);

  const classes = useTrackingGridStyles({
    height,
    isDesktopTopSheetOpen,
    isFilterSelected
  });
  const rowClickDispatchFn = useCallback(debounce((data) => {
    if (data) {
      dispatch(setSelectedAsset({
        asset       : data,
        useCtc      : !showAssetsListRef.current,
        resetTool   : true,
        coordinates : null
      }) as any);
    }
  }, 300), []);

  const onRowClickHandler = onAssetRowClicked(rowClickDispatchFn, rowClicked);

  useEffect(() => {
    const gridRowCount = gridApi?.getDisplayedRowCount();
    if (assetList?.length !== gridRowCount) {
      const data = !findNearbyIsActiveRef.current
        ? mergeLatestUpdates(assetsRef.current, assetList) : assetList;
      setAssetsList(data);
    }
    assetsListRef.current = assetList;
  }, [gridApi, assetList]);

  useEffect(() => {
    if (!findNearbyIsActiveRef.current) {
      if (assets?.length) {
        setAssetsList(mergeLatestUpdates(assets, assetsListRef.current));
      } else {
        setAssetsList([]);
      }
    }
    assetsRef.current = assets;
  }, [assets]);

  useEffect(() => {
    mediaRef.current = {isDesktop, isPhone};
  }, [isDesktop, isPhone]);

  // Set the grid API so we can use it to find rows that are clicked outside the grid.
  const onGridReady = useCallback((api) => {
    setGridApi(api);
  }, []);

  useEffect(function() {
    if (assetsUpdates?.length > 0 && !findNearbyIsActiveRef.current) {
      applyTransaction(assetsUpdates, applySelection);
    }
  }, [assetsUpdates]);

  useEffect(function() {
    if (scDevicesUpdates?.length > 0 && !findNearbyIsActiveRef.current) {
      applyTransaction(scDevicesUpdates, applySelection);
    }
  }, [scDevicesUpdates]);

  const applyTransaction = useCallback(function(assets: Device[] | SCDeviceType[], callback: Function) {
    const updatesRows = assets.map((asset) => constructCVSGridRows(asset, eventListRef, true));
    gridApi?.applyTransactionAsync?.({update: updatesRows}, function() {
      callback?.();
    });
  }, [gridApi]);

  const applySelection = useCallback(function() {
    if (!isNil(selectedAssetRef.current)) {
      gridApi?.forEachNode(function(rowNode) {
        if (rowNode.data.id === selectedAssetRef.current.id()) {
          rowNode.setSelected(true);
        } else {
          rowNode.setSelected(false);
        }
      });
    } else {
      gridApi?.clearFocusedCell?.();
      gridApi?.deselectAll?.();
    }
  }, [gridApi]);

  useEffect(applySelection, [selectedAsset]);

  const onFilterChanged = useCallback(function(data) {
    const filterModel = gridApi?.getFilterModel();
    if (filterModel) {
      batch(() => {
        dispatch(setFilterModel(filterModel));
        dispatch(setGridChange() as any);
      });
    }
    if (!isLargeFleetMode) {
      const {api} = data;
      const filteredRows = getDisplayedRows(api);
      if (Array.isArray(filteredRows)) {
        batch(() => {
          dispatch(setGridFilteredAssetIds(filteredRows) as any);
          dispatch(setIsAssetGridUpdate(true) as any);
          if (!hasSelectedAsset(filteredRows) &&
            verifyDevicesLoaded(hasDevicesAccessRef.current, hasDeviceLoadedRef.current)) {
            api?.deselectAll();
            dispatch(setSelectedAsset({asset: null}) as any);
          }
        });
      }
    }
  }, [gridApi]);

  /**
   * Utility to validate if an Asset Id exists in an a filtered list of Assets.
   */
  const hasSelectedAsset = (assets: CVSAssetType[]) => {
    return assets?.some(({id}) => id === selectedAssetRef.current?.id());
  };

  const verifyDevicesLoaded = (hasDevicesAccess: boolean, hasDeviceLoaded: boolean) => {
    return hasDevicesAccess ? hasDeviceLoaded : true;
  };

  const getColumnDefinitions = (unitsInHeader: Unit) => ({
    desktop : resolveDefinitions(getColumnDef({
      includesAssets,
      includesScDevices,
      findNearbyIsActive,
      unitsInHeader,
      sortComparator
    })),
    mobile : resolveDefinitions(getColumnDef({
      includesAssets    : false,
      includesScDevices : true,
      findNearbyIsActive,
      unitsInHeader,
      sortComparator
    }))
  });

  const resolverOptions = useMemo(() => {
    return {findNearbyIsActive};
  }, [findNearbyIsActive]);

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(unitsInHeader),
    resolver          : columnResolver,
    columnResolver    : columnNormalizer,
    resolverOptions,
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(
    verifyDistanceColumn(definitions, findNearbyIsActive),
    columnNormalizer,
    resolverOptions
  );

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    onExport,
    onRefresh,
    onClearFilters
  }));

  const onExport = function() {
    const params = ionGridRef.current?.getExportParams();
    if (!isLargeFleetMode) {
      const {items} = ionGridRef.current?.getAllDisplayedRowData();
      dispatch(exportTrackingAssets({...params, items, augmenter: constructAssetHistoryGridRows}) as any);
    } else {
      dispatch(exportTrackingAssets({...params, augmenter: constructAssetHistoryGridRows}) as any);
    }
  };

  const onClearFilters = useCallback(() => {
    ionGridRef.current?.onClearFilters();
  }, [ionGridRef]);

  const onRefresh = useCallback(() => {
    setRefreshRequired(true);
  }, []);

  const onColumnSelectionsUpdate = useCallback(function(columns: ColDef[]): void {
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GRID_COLUMNS) as any);
  }, []);

  const onColumnStateUpdate = useCallback(function(state: ColumnState[]): void {
    state && dispatch(dispatchGridColumnsChange(state, NAME, SET_GRID_COLUMNS) as any);
  }, []);

  const constructAssetHistoryGridRows = useCallback(function(items, isExport = false): GridResult[] {
    return items?.map(item => constructCVSGridRows(item, eventListRef, isExport));
  }, []);

  /**
   * @private
   */
  const onAssetClick = useCallback(function({data}) {
    if (data) {
      const selectedId    = Number(getRowNodeId({data}));
      const {items}       = ionGridRef?.current?.getAllDisplayedRowData();
      const selectedAsset = items?.find((item: any) => Number(getRowNodeId({data: item})) === selectedId);
      dispatch(onAssetSelected({asset: selectedAsset, media: mediaRef.current, resetTool: true}) as any);
    }
  }, []);

  const customScrollTop: string = useMemo(() => {
    return isDesktop
      ? `${(document.getElementById('trackingFilterWrapper') as HTMLElement)?.offsetHeight}px`
      : '0px';
  }, []);

  const getGridHeight = useCallback(() => {
    return classes.agGridAssetTracking;
  }, []);

  const getRowNodeId = useCallback(({data}) => {
    return String(data?.isScDevice ? data?.deviceIdValue : data?.assetId);
  }, []);

  const onColumnChooserClose = useCallback(() => {
    toggleColumnChooser(false);
  }, []);

  const selectAndScrollToRow = useCallback(debounce((gridApi) => {
    scrollToItem?.(gridApi, selectedAsset);
  }, scrollToInterval.clientMode), [selectedAsset]);

  const hiddenColumns = useMemo(() => !findNearbyIsActive ? HiddenColumns : [], [findNearbyIsActive]);

  return (
    <>
      <IonGrid ref={ionGridRef}
        isDeltaRowDataMode={true}
        onLoadingChange={onLoading}
        onFirstDataRendered={function(event) {
          selectAndScrollToRow?.(event.api);
        }}
        isLoading={loading || isLoading}
        onGridReady={onGridReady}
        customScrollTop={customScrollTop}
        filterModelConverter={filterModelConverterV2}
        sortModelConverter={sortModelConverter}
        refreshRequired={refreshRequired}
        setRefreshRequired={setRefreshRequired}
        totalRows={assetsList?.length || 0}
        rowData={assetsList}
        rowModelType={RowModelTypes.ClientSide}
        onRowClicked={onRowClickHandler}
        getRowNodeId={getRowNodeId}
        getGridHeight={getGridHeight}
        onFilterChanged={onFilterChanged}
        augmentItems={constructAssetHistoryGridRows}
        attributeMappings={attributeMappings}
        defaultSort={defaultSort}
        showColumnChooser={showColumnChooser}
        columnDefinitions={columnDefinitions}
        onColumnChooserSave={onColumnSelectionsUpdate}
        onColumnsUpdated={onColumnStateUpdate}
        onColumnChooserClose={onColumnChooserClose}
        rowMenusEnabled={false}
        defaultDefinitions={defaultDefinitions}
        filterModel={filterModel}
        selectedIds={[selectedAsset?.OBJECTID()]}
        hiddenColumns={hiddenColumns}
        columnComponents={{
          assetType : AssetTypeCell,
          speedCell : ({data: {heading, speedUI} = {}}: {data: any}) => (
            <SpeedCell heading={heading} value={speedUI} />
          ),
          gpsSpeedCell : ({data: {heading, gpsSpeedUI} = {}}: {data: any}) => (
            <SpeedCell heading={heading} value={gpsSpeedUI} />
          ),
          vbSpeedCell : ({data: {heading, vbSpeedUI} = {}}: {data: any}) => (
            <SpeedCell heading={heading} value={vbSpeedUI} />
          ),
          assetName : (data: any) => (
            <LinkCell data = {data}
              onClick={onAssetClick} />
          ),
          eventTimeUI : ({data}) => data?.eventTimeUI || null
        }}
        checkboxesEnabled={false}
        omitHeader
        suppressScrollOnNewData />
    </>
  );
};

/**
 * constructCVSGridRows formats the ArcGIS raw data into user
 * readable format/form.
 */
export const constructCVSGridRows = (
  row: Device,
  eventCodeList: MutableRefObject<any> | EventCode[],
  isExport = false,
  useDateFormatter = false
// eslint-disable-next-line max-params
) => {
  const device    = row?.getDevice?.() || row;
  const eventList = eventCodeList?.current || eventCodeList;
  const onLabel   = translate('common:switchStatus.on');
  const offLabel  = translate('common:switchStatus.off');
  const parked    = translate('common:assetState.Parked');
  const item      = getItemValue(device);
  const {gpsSpeed, odometer, gpsOdometer, vbSpeed, vbOdometer, altitude} =
        convertAVLEvent({...item}) as CVSAssetType ?? {};
  const {
    operatorNames,
    street,
    city,
    state,
    postalCode,
    assetType,
    eventTime,
    engineHour,
    engineHours,
    fuelType,
    idleHours,
    vbusMilStatus,
    adcDeviceBatteryVoltage,
    adcExternalPowerVoltage,
    batteryLevelValue,
    ignition,
    input1,
    input2,
    input3,
    input4,
    input5,
    input6,
    input7,
    input8,
    gpsValidity,
    commStateAvailable,
    postedSpeedLimit,
    latitude,
    location,
    longitude,
    routeName,
    assetName,
    isScDevice,
    deviceEsn,
    deviceName,
    deviceType,
    deviceIdValue
  } = item;
  const batteryValue = getBattery(item);
  const stateLabel   = getAssetStateLabel(getAssetState(item));
  return {
    ...item,
    operatorNamesUI : operatorNames,
    stateUI         : stateLabel,
    addressUI       : formatAddress({street, city, state, zipcode: postalCode}, true),
    assetType       : assetType === AssetType.Default ? '' : assetType,
    eventTimeUI     : useDateFormatter ? dateFormatter(eventTime): dateTimeFormatter(eventTime),
    associations    : getAssociations(item),
    latestEventUI   : eventList ? getInputChangedEvents(
      item,
      getEventName({
        source : eventList,
        from   : EventCodeValueType.eventCode,
        val    : item.eventCode
      })
    ) : null,
    eventTypeUI : eventList ? getEventType({
      source : eventList,
      from   : EventCodeValueType.eventCode,
      val    : item.eventCode
    }) : null,
    odometerUI           : toFixed(odometer, 1),
    gpsOdometerUI        : toFixed(gpsOdometer, 1),
    vbOdometerUI         : toFixed(vbOdometer, 1),
    engineHourUI         : secondsToHours(engineHour ?? 0),
    engineHoursGPSUI     : secondsToHours(engineHours ?? 0),
    fuelTypeUI           : getEngineType(fuelType),
    idleHoursUI          : secondsToHours(idleHours ?? 0),
    engineLightUI        : booleanToSwitchString(vbusMilStatus, onLabel, offLabel)?.toUpperCase(),
    vbatIntUI            : getBatteryVoltageFixed(adcDeviceBatteryVoltage ?? 0),
    vbatExtUI            : getBatteryVoltageFixed(adcExternalPowerVoltage ?? 0),
    ignitionUI           : getInputsOnOffLabel(ignition),
    input1UI             : getInputsOnOffLabel(input1),
    input2UI             : getInputsOnOffLabel(input2),
    input3UI             : getInputsOnOffLabel(input3),
    input4UI             : getInputsOnOffLabel(input4),
    input5UI             : getInputsOnOffLabel(input5),
    input6UI             : getInputsOnOffLabel(input6),
    input7UI             : getInputsOnOffLabel(input7),
    postedSpeedLimit,
    gpsValidityUI        : booleanToSwitchString(gpsValidity, onLabel, offLabel)?.toUpperCase(),
    commStateAvailableUI : booleanToSwitchString(commStateAvailable, onLabel, offLabel)?.toUpperCase(),
    gpsSpeedUI           : getSpeedByStatus(gpsSpeed, parked, stateLabel),
    vbSpeedUI            : getSpeedByStatus(vbSpeed, parked, stateLabel),
    latitudeUI           : toFixed(latitude, 7),
    longitudeUI          : toFixed(longitude, 7),
    speedUI              : getSpeedByStatus(getEventSpeed(item).speed, parked, stateLabel),
    altitudeUI           : altitude,
    routeNameUI          : titleCase(routeName),
    location             : location,
    assetNameUI          : assetName,
    deviceEsnUI          : deviceIdValue,
    deviceNameUI         : deviceName ?? '',
    deviceType           : deviceType ?? '',
    batteryUI            : !isNil(batteryValue) ? Number(batteryValue) : batteryValue
  };
};

/**
   * @getColumnDef
   *
   * Returns column definitions for the desktop grid implementation ...
   */
const getColumnDef = ({
  includesAssets,
  includesScDevices,
  findNearbyIsActive,
  unitsInHeader,
  sortComparator
}): ColDef[] => {
  const getUOMColumn = (units: Unit, metric: string, imperial: string): string => (
    translate(`tracking:tableView.grid.columns.${isMetric(unitsInHeader) ? metric : imperial}`)
  );

  const columns = [{
    headerName   : translate('tracking:tableView.grid.columns.group'),
    field        : 'accountName',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.asset'),
    field        : 'assetNameUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams,
    cellRenderer : 'assetName',
    comparator   : sortComparator
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.deviceName'),
    field        : 'deviceNameUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : !includesAssets && includesScDevices,
    filterParams : defaultCVSFilterParams,
    cellRenderer : 'assetName',
    comparator   : sortComparator
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.deviceType'),
    field        : 'deviceType',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.time'),
    field        : 'eventTimeUI',
    cellRenderer : 'eventTimeUI',
    sortable     : true,
    filter       : 'date',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : ClientISODateFilterParams,
    comparator   : clientGridDateTimeComparator
  },
  {
    headerName : translate('tracking:tableView.grid.columns.latestEvent'),
    field      : 'latestEventUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.eventType'),
    field        : 'eventTypeUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.address'),
    field        : 'addressUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.geofenceLocation'),
    field        : 'location',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.state'),
    field        : 'stateUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : caseInSensitiveEqualsFilterParams
  },
  {
    headerName : translate('tracking:tableView.grid.columns.assignments'),
    field      : 'associations',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName         : getUOMColumn(unitsInHeader, 'speedKmph', 'speedMph'),
    field              : 'speedUI',
    cellRenderer       : 'speedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'odometerKm', 'odometerMi'),
    field      : 'odometerUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineHours'),
    field      : 'engineHourUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.identifierType'),
    field        : 'deviceIdType',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.esn'),
    field        : 'deviceEsnUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.alias'),
    field        : 'assetAlias',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName         : translate('tracking:tableView.grid.columns.type'),
    field              : 'assetType',
    cellRenderer       : 'assetType',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.category'),
    field        : 'assetCategory',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.engineType'),
    field        : 'fuelTypeUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.driver'),
    field        : 'operatorNamesUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.driverType'),
    field        : 'operatorType',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.driverCategory'),
    field        : 'operatorCategory',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'postedSpeedLimitKm', 'postedSpeedLimit'),
    field      : 'postedSpeedLimit',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName         : getUOMColumn(unitsInHeader, 'speedGPSkmph', 'speedGPSmph'),
    field              : 'gpsSpeedUI',
    cellRenderer       : 'gpsSpeedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  },
  {
    headerName         : getUOMColumn(unitsInHeader, 'speedVBkmph', 'speedVBmph'),
    field              : 'vbSpeedUI',
    cellRenderer       : 'vbSpeedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'odometerGPSkm', 'odometerGPSmi'),
    field      : 'gpsOdometerUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'odometerVBkm', 'odometerVBmi'),
    field      : 'vbOdometerUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineHoursGPS'),
    field      : 'engineHoursGPSUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineHoursVB'),
    field      : 'vbEngineHours',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.idleHours'),
    field      : 'idleHoursUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineLight'),
    field      : 'engineLightUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.fuelLevel'),
    field      : 'vbFuelPercent',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.battery'),
    field      : 'batteryUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.deviceBattery'),
    field      : 'batteryRemainingPercent',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.vbatInt'),
    field      : 'vbatIntUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.vbatExt'),
    field      : 'vbatExtUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.ignition'),
    field      : 'ignitionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input1'),
    field      : 'input1UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input2'),
    field      : 'input2UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input3'),
    field      : 'input3UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input4'),
    field      : 'input4UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input5'),
    field      : 'input5UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input6'),
    field      : 'input6UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input7'),
    field      : 'input7UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.inputMap'),
    field        : 'inputBitmap',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName : translate('tracking:tableView.grid.columns.gpsValidity'),
    field      : 'gpsValidityUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.commStateAvailable'),
    field      : 'commStateAvailableUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.latitude'),
    field      : 'latitudeUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.longitude'),
    field      : 'longitudeUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'altitudeM', 'altitude'),
    field      : 'altitudeUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.routeName'),
    field        : 'routeNameUI',
    sortable     : true,
    resizable    : true,
    filter       : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName : translate('tracking:tableView.grid.columns.eventSequence'),
    field      : 'deviceMessageSequenceNumber',
    sortable   : true,
    resizable  : true,
    filter     : false,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineRpm'),
    field      : 'VBEngineSpeed',
    sortable   : true,
    resizable  : true,
    filter     : false,
    unSortIcon : true,
    hide       : true
  }];
  return verifyDistanceColumn(columns, findNearbyIsActive);
};

/**
 * Filters assets based on a grid filter model.
 *
 * @param assets - The array of assets to be filtered.
 * @param filterModel - The grid filter model used for filtering.
 * @returns The filtered array of assets.
 *
 * ```typescript
 *
 * // filtering assets based on a grid filter model
 * const filteredAssets = filterAssetsByGridModel(allAssets, filterModel);
 * console.log(filteredAssets);
 *
 * ```
 */
export const filterAssetsByGridModel = (assets: Device[], filterModel: GenericObject, from?: number) => {
  if (!isEmpty(filterModel)) {
    return filterData(assets || [], filterModel, constructCVSGridRows) || [];
  }
  return assets;
};

export default forwardRef(TrackingAssetGrid);
